import React, { useState } from 'react';
import Select from 'react-select';
import { useData } from '../contexts/DataContext';

function Models({ showToast }) {
  // Use centralized data from context
  const { 
    models, 
    addModel, 
    updateModel, 
    deleteModel, 
    toggleModelStatus,
    getActiveIndicators,
    getIndicatorById,
    customers,
    customerGroups
  } = useData();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['active']);
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [quickViewModel, setQuickViewModel] = useState(null);
  const [editingModel, setEditingModel] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    targetObject: 'Khách hàng cá nhân',
    calculationMethod: 'weighted_sum',
    indicators: [],
    thresholds: {
      high: { min: 4 },
      medium: { min: 2, max: 3.99 },
      low: { min: 1, max: 1.99 },
      none: { max: 0.99 }
    },
    assignedCustomers: [],
    assignedCustomerGroups: [],
    status: 'active' // 'draft' | 'active' | 'inactive'
  });

  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [indicatorWeight, setIndicatorWeight] = useState(0.1);
  const [indicatorFrequency, setIndicatorFrequency] = useState('Hàng tháng');
  const [indicatorSearchTerm, setIndicatorSearchTerm] = useState('');
  const [selectedIndicatorCategory, setSelectedIndicatorCategory] = useState('');
  const [activeAssignTab, setActiveAssignTab] = useState('customers');
  const [activeFormTab, setActiveFormTab] = useState('info'); // 'info' | 'indicators' | 'assignment'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddIndicator = () => {
    if (!selectedIndicator) {
      showToast('Vui lòng chọn chỉ tiêu', 'warning');
      return;
    }
    if (formData.indicators.some(ind => ind.id === selectedIndicator.value)) {
      showToast('Chỉ tiêu này đã được thêm', 'warning');
      return;
    }

    // Get full indicator details from context
    const indicatorDetails = getIndicatorById(selectedIndicator.value);
    
    const newIndicator = {
      id: selectedIndicator.value,
      weight: parseFloat(indicatorWeight),
      frequency: indicatorFrequency
    };
    
    setFormData(prev => ({
      ...prev,
      indicators: [...prev.indicators, newIndicator]
    }));
    
    showToast(`Đã thêm chỉ tiêu: ${indicatorDetails?.name}`, 'success');
    setSelectedIndicator(null);
    setIndicatorWeight(0.1);
  };

  const getTotalWeight = () => {
    return formData.indicators.reduce((sum, ind) => sum + ind.weight, 0).toFixed(2);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!formData.name || formData.indicators.length === 0) {
      showToast('Vui lòng điền tên và thêm ít nhất 1 chỉ tiêu', 'warning');
      return;
    }
    if (Math.abs(parseFloat(getTotalWeight()) - 1.0) > 0.01) {
      showToast(`Tổng trọng số phải = 1.0 (hiện tại: ${getTotalWeight()})`, 'warning');
      return;
    }
    
    const modelData = { ...formData, status: 'active' };
    
    if (editingModel) {
      updateModel(editingModel.id, modelData);
      showToast('Đã cập nhật mô hình', 'success');
    } else {
      addModel(modelData);
      showToast('Đã tạo mô hình mới', 'success');
    }
    setShowModal(false);
    setActiveFormTab('info');
  };

  const handleSaveDraft = () => {
    if (!formData.name) {
      showToast('Vui lòng nhập tên mô hình trước khi lưu nháp', 'warning');
      return;
    }
    
    const draftData = { ...formData, status: 'draft' };
    
    if (editingModel) {
      updateModel(editingModel.id, draftData);
      showToast('Đã lưu nháp mô hình', 'info');
    } else {
      addModel(draftData);
      showToast('Đã lưu nháp mô hình mới', 'info');
    }
    setShowModal(false);
    setActiveFormTab('info');
  };

  // Wizard navigation helpers
  const canProceedToNextTab = () => {
    switch (activeFormTab) {
      case 'info':
        return formData.name && formData.name.trim() !== '';
      case 'indicators':
        return formData.indicators.length > 0 && Math.abs(parseFloat(getTotalWeight()) - 1.0) < 0.01;
      case 'assignment':
        return true; // Assignment is optional
      default:
        return false;
    }
  };

  const handleNextTab = () => {
    if (!canProceedToNextTab()) {
      if (activeFormTab === 'info') {
        showToast('Vui lòng nhập tên mô hình', 'warning');
      } else if (activeFormTab === 'indicators') {
        if (formData.indicators.length === 0) {
          showToast('Vui lòng thêm ít nhất 1 chỉ tiêu', 'warning');
        } else {
          showToast('Tổng trọng số phải = 1.0', 'warning');
        }
      }
      return;
    }

    if (activeFormTab === 'info') setActiveFormTab('indicators');
    else if (activeFormTab === 'indicators') setActiveFormTab('assignment');
  };

  const handlePrevTab = () => {
    if (activeFormTab === 'indicators') setActiveFormTab('info');
    else if (activeFormTab === 'assignment') setActiveFormTab('indicators');
  };

  const getTabIcon = (tab) => {
    switch (tab) {
      case 'info': return 'bi-info-circle';
      case 'indicators': return 'bi-bar-chart';
      case 'assignment': return 'bi-people';
      default: return 'bi-circle';
    }
  };

  const getTabLabel = (tab) => {
    switch (tab) {
      case 'info': return 'Thông tin mô hình';
      case 'indicators': return 'Gán chỉ tiêu & Rủi ro';
      case 'assignment': return 'Gán khách hàng';
      default: return '';
    }
  };

  const isTabCompleted = (tab) => {
    switch (tab) {
      case 'info':
        return formData.name && formData.name.trim() !== '';
      case 'indicators':
        return formData.indicators.length > 0 && Math.abs(parseFloat(getTotalWeight()) - 1.0) < 0.01;
      case 'assignment':
        return (formData.assignedCustomers && formData.assignedCustomers.length > 0) ||
               (formData.assignedCustomerGroups && formData.assignedCustomerGroups.length > 0);
      default:
        return false;
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Xóa mô hình?')) {
      deleteModel(id);
      showToast('Đã xóa', 'success');
    }
  };

  const handleToggleStatus = (id) => {
    toggleModelStatus(id);
    showToast('Đã cập nhật trạng thái', 'success');
  };

  const filteredModels = models.filter(m => {
    const matchSearch = !searchTerm || m.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTarget = selectedTargets.length === 0 || selectedTargets.includes(m.targetObject);
    const matchStatus = selectedStatuses.length === 0 || selectedStatuses.includes(m.status);
    return matchSearch && matchTarget && matchStatus;
  });

  const stats = {
    total: models.length,
    active: models.filter(m => m.status === 'active').length,
    inactive: models.filter(m => m.status === 'inactive').length,
    avgIndicators: models.length > 0 ? (models.reduce((sum, m) => sum + m.indicators.length, 0) / models.length).toFixed(1) : 0,
    retail: models.filter(m => m.targetObject === 'Khách hàng cá nhân').length,
    corporate: models.filter(m => m.targetObject === 'Khách hàng doanh nghiệp').length
  };

  // Get available indicators from context
  const availableIndicators = getActiveIndicators();
  
  // Group indicators by category for dropdown
  const groupedIndicatorOptions = availableIndicators.reduce((groups, ind) => {
    const category = ind.category || 'Khác';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push({
      value: ind.id,
      label: ind.name,
      searchLabel: `${ind.id} - ${ind.name}`, // For search
      category: category
    });
    return groups;
  }, {});

  // Convert to react-select grouped format
  const indicatorOptions = Object.entries(groupedIndicatorOptions).map(([category, items]) => ({
    label: category,
    options: items
  }));

  // Custom format for group labels
  const formatGroupLabel = (data) => {
    const categoryColors = {
      'Tài chính': 'success',
      'Thanh toán': 'primary',
      'Hành vi': 'warning',
      'Bên ngoài': 'info',
      'Khác': 'secondary'
    };
    const color = categoryColors[data.label] || 'secondary';
    
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        fontWeight: 'bold',
        color: `var(--bs-${color})`,
        textTransform: 'uppercase',
        fontSize: '0.85em',
        padding: '8px 0 4px 0',
        borderBottom: `2px solid var(--bs-${color})`,
        marginBottom: '4px'
      }}>
        <span>{data.label}</span>
        <span style={{
          background: `var(--bs-${color})`,
          color: 'white',
          borderRadius: '10px',
          padding: '2px 8px',
          fontSize: '0.9em',
          fontWeight: 'normal'
        }}>
          {data.options.length}
        </span>
      </div>
    );
  };

  // Custom filter to search across ID, name, and category
  const customFilterOption = (option, searchText) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      option.data.searchLabel.toLowerCase().includes(search) ||
      option.data.category.toLowerCase().includes(search)
    );
  };

  // Custom format for option display
  const formatOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ 
        fontSize: '0.85em', 
        color: '#6c757d',
        fontFamily: 'monospace',
        fontWeight: '600'
      }}>
        {option.value}
      </span>
      <span style={{ color: '#212529' }}>
        {option.label}
      </span>
    </div>
  );

  // Helper to get indicator name by ID
  const getIndicatorName = (id) => {
    const indicator = getIndicatorById(id);
    return indicator ? indicator.name : id;
  };

  return (
    <div className="container-fluid">
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-diagram-3 me-2"></i>Quản lý Mô hình cảnh báo sớm
            </h2>
            <p className="text-muted mb-0">Tạo và quản lý mô hình đánh giá rủi ro từ tổ hợp chỉ tiêu</p>
          </div>
          <div>
            <button className="btn btn-primary" onClick={() => { 
              setEditingModel(null); 
              setFormData({ 
                name: '', 
                description: '', 
                targetObject: 'Khách hàng cá nhân', 
                calculationMethod: 'weighted_sum', 
                indicators: [], 
                thresholds: { 
                  high: {min: 4}, 
                  medium: {min:2, max:3.99}, 
                  low: {min:1, max:1.99}, 
                  none: {max:0.99} 
                } 
              }); 
              setShowModal(true); 
            }}>
              <i className="bi bi-plus-circle me-2"></i>Tạo mô hình mới
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Statistics */}
      <div className="row mb-4 stagger-animation">
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #6c757d'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Tổng mô hình</h6>
                  <h3 className="mb-0 fw-bold">{stats.total}</h3>
                </div>
                <div className="bg-secondary bg-opacity-10 rounded p-2">
                  <i className="bi bi-diagram-3-fill text-secondary" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #28a745'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Đang hoạt động</h6>
                  <h3 className="mb-0 fw-bold text-success">{stats.active}</h3>
                </div>
                <div className="bg-success bg-opacity-10 rounded p-2">
                  <i className="bi bi-check-circle-fill text-success" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #dc3545'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Tạm dừng</h6>
                  <h3 className="mb-0 fw-bold text-danger">{stats.inactive}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 rounded p-2">
                  <i className="bi bi-pause-circle-fill text-danger" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #0dcaf0'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>TB chỉ tiêu/mô hình</h6>
                  <h3 className="mb-0 fw-bold text-info">{stats.avgIndicators}</h3>
                </div>
                <div className="bg-info bg-opacity-10 rounded p-2">
                  <i className="bi bi-bar-chart-fill text-info" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #0d6efd'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Cá nhân</h6>
                  <h3 className="mb-0 fw-bold text-primary">{stats.retail}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 rounded p-2">
                  <i className="bi bi-person-fill text-primary" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #6610f2'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Doanh nghiệp</h6>
                  <h3 className="mb-0 fw-bold" style={{color: '#6610f2'}}>{stats.corporate}</h3>
                </div>
                <div className="rounded p-2" style={{backgroundColor: 'rgba(102, 16, 242, 0.1)'}}>
                  <i className="bi bi-building-fill" style={{fontSize: '1.5rem', color: '#6610f2'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Global Search */}
            <div className="col-lg-4">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Tìm theo tên, mã, mô tả..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Target Object Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'Khách hàng cá nhân', label: '👤 Cá nhân' },
                  { value: 'Khách hàng doanh nghiệp', label: '🏢 Doanh nghiệp' },
                  { value: 'Các ĐCTC', label: '🏦 ĐCTC' }
                ]}
                value={selectedTargets.map(t => ({ value: t, label: t }))}
                onChange={(selected) => setSelectedTargets(selected ? selected.map(s => s.value) : [])}
                placeholder="Đối tượng..."
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* Status Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'active', label: '✅ Hoạt động' },
                  { value: 'inactive', label: '❌ Tạm dừng' }
                ]}
                value={selectedStatuses.map(s => ({ value: s, label: s === 'active' ? '✅ Hoạt động' : '❌ Tạm dừng' }))}
                onChange={(selected) => setSelectedStatuses(selected ? selected.map(s => s.value) : [])}
                placeholder="Trạng thái..."
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* View Mode Toggle */}
            <div className="col-lg-2">
              <div className="btn-group w-100" role="group">
                <button
                  className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title="Xem dạng lưới"
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}
                  title="Xem dạng danh sách"
                >
                  <i className="bi bi-list-ul"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Xem dạng bảng"
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || selectedTargets.length > 0 || (selectedStatuses.length > 0 && selectedStatuses.length < 2)) && (
            <div className="mt-3 d-flex align-items-center flex-wrap gap-2">
              <small className="text-muted">Đang lọc:</small>
              {searchTerm && (
                <span className="badge bg-primary">
                  Tìm kiếm: "{searchTerm}"
                  <i className="bi bi-x ms-1 cursor-pointer" style={{cursor: 'pointer'}} onClick={() => setSearchTerm('')}></i>
                </span>
              )}
              {selectedTargets.map(target => (
                <span key={target} className="badge bg-info">
                  {target}
                  <i className="bi bi-x ms-1 cursor-pointer" style={{cursor: 'pointer'}} onClick={() => setSelectedTargets(selectedTargets.filter(t => t !== target))}></i>
                </span>
              ))}
              {selectedStatuses.map(status => (
                <span key={status} className={`badge bg-${status === 'active' ? 'success' : 'secondary'}`}>
                  {status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                  <i className="bi bi-x ms-1 cursor-pointer" style={{cursor: 'pointer'}} onClick={() => setSelectedStatuses(selectedStatuses.filter(s => s !== status))}></i>
                </span>
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedTargets([]);
                  setSelectedStatuses([]);
                }}
              >
                Xóa tất cả
              </button>
            </div>
          )}

          {/* Results count */}
          <div className="mt-3">
            <small className="text-muted">
              Hiển thị <strong>{filteredModels.length}</strong> / {models.length} mô hình
            </small>
          </div>
        </div>
      </div>

      {/* Models Display */}
      {filteredModels.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox" style={{fontSize: '48px', opacity: 0.3}}></i>
            <p className="text-muted mt-3 mb-0">Không tìm thấy mô hình nào</p>
          </div>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="row g-4 stagger-animation">
              {filteredModels.map(model => (
                <div key={model.id} className="col-lg-4 col-md-6">
                  <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: `4px solid ${model.status === 'active' ? '#28a745' : '#6c757d'}`}}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3">
                        <span className={`badge bg-${model.status === 'active' ? 'success' : 'secondary'}`}>
                          {model.status === 'active' ? '✅ Hoạt động' : '⏸️ Tạm dừng'}
                        </span>
                        <small className="text-muted">{model.id}</small>
                      </div>
                      <h5 className="card-title">{model.name}</h5>
                      <p className="text-muted small" style={{minHeight: '40px'}}>{model.description}</p>
                      <div className="mb-3">
                        <small><i className="bi bi-people me-1"></i>{model.targetObject}</small><br/>
                        <small><i className="bi bi-bar-chart me-1"></i>{model.indicators.length} chỉ tiêu</small><br/>
                        {model.assignedCustomers && model.assignedCustomers.length > 0 && (
                          <small className="text-primary">
                            <i className="bi bi-person-check me-1"></i>
                            {model.assignedCustomers.length} khách hàng
                          </small>
                        )}
                        {model.assignedCustomers && model.assignedCustomers.length > 0 && 
                         model.assignedCustomerGroups && model.assignedCustomerGroups.length > 0 && (
                          <span className="text-muted"> • </span>
                        )}
                        {model.assignedCustomerGroups && model.assignedCustomerGroups.length > 0 && (
                          <small className="text-warning">
                            <i className="bi bi-people-fill me-1"></i>
                            {model.assignedCustomerGroups.length} nhóm
                          </small>
                        )}
                      </div>
                      <div className="row g-2 mb-3">
                        <div className="col-6">
                          <div className="p-2 rounded text-center" style={{backgroundColor: '#fee', border: '1px solid #fcc'}}>
                            <small className="text-danger fw-bold">≥{model.thresholds.high.min}</small>
                            <div className="small text-muted">Cao</div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="p-2 rounded text-center" style={{backgroundColor: '#e6ffe6', border: '1px solid #b3ffb3'}}>
                            <small className="text-success fw-bold">&lt;{model.thresholds.none.max}</small>
                            <div className="small text-muted">OK</div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-primary flex-fill" onClick={() => { setQuickViewModel(model); setShowQuickViewModal(true); }} title="Xem nhanh">
                          <i className="bi bi-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => { setEditingModel(model); setFormData({...model}); setShowModal(true); }} title="Sửa">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className={`btn btn-sm btn-outline-${model.status === 'active' ? 'warning' : 'success'} flex-fill`} onClick={() => handleToggleStatus(model.id)} title={model.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}>
                          <i className={`bi bi-${model.status === 'active' ? 'pause' : 'play'}-circle`}></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger flex-fill" onClick={() => handleDelete(model.id)} title="Xóa">
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="row g-3 stagger-animation">
              {filteredModels.map(model => (
                <div key={model.id} className="col-12">
                  <div className="card border-0 shadow-sm hover-lift" style={{borderLeft: `4px solid ${model.status === 'active' ? '#28a745' : '#6c757d'}`}}>
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <div className="d-flex align-items-start gap-3">
                            <div className={`badge bg-${model.status === 'active' ? 'success' : 'secondary'} align-self-start`}>
                              {model.status === 'active' ? '✅' : '⏸️'}
                            </div>
                            <div className="flex-grow-1">
                              <h5 className="mb-1">{model.name}</h5>
                              <small className="text-muted d-block mb-2">{model.id}</small>
                              <p className="text-muted small mb-2">{model.description}</p>
                              <div className="d-flex gap-3 flex-wrap">
                                <small><i className="bi bi-people me-1"></i>{model.targetObject}</small>
                                <small><i className="bi bi-bar-chart me-1"></i>{model.indicators.length} chỉ tiêu</small>
                                {model.assignedCustomers && model.assignedCustomers.length > 0 && (
                                  <small className="text-primary">
                                    <i className="bi bi-person-check me-1"></i>
                                    {model.assignedCustomers.length} KH
                                  </small>
                                )}
                                {model.assignedCustomerGroups && model.assignedCustomerGroups.length > 0 && (
                                  <small className="text-warning">
                                    <i className="bi bi-people-fill me-1"></i>
                                    {model.assignedCustomerGroups.length} nhóm
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="d-flex gap-2 justify-content-center">
                            <div className="text-center px-3">
                              <div className="text-danger fw-bold">≥{model.thresholds.high.min}</div>
                              <small className="text-muted">Cao</small>
                            </div>
                            <div className="text-center px-3">
                              <div className="text-warning fw-bold">{model.thresholds.medium.min}-{model.thresholds.medium.max}</div>
                              <small className="text-muted">TB</small>
                            </div>
                            <div className="text-center px-3">
                              <div className="text-success fw-bold">&lt;{model.thresholds.none.max}</div>
                              <small className="text-muted">OK</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-2">
                          <div className="d-flex gap-2 justify-content-end">
                            <button className="btn btn-sm btn-outline-primary" onClick={() => { setQuickViewModel(model); setShowQuickViewModal(true); }} title="Xem nhanh">
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setEditingModel(model); setFormData({...model}); setShowModal(true); }} title="Sửa">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className={`btn btn-sm btn-outline-${model.status === 'active' ? 'warning' : 'success'}`} onClick={() => handleToggleStatus(model.id)} title={model.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}>
                              <i className={`bi bi-${model.status === 'active' ? 'pause' : 'play'}-circle`}></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(model.id)} title="Xóa">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="card border-0 shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Trạng thái</th>
                      <th>Mã</th>
                      <th>Tên mô hình</th>
                      <th>Đối tượng</th>
                      <th className="text-center">Số chỉ tiêu</th>
                      <th className="text-center">Gán KH/Nhóm</th>
                      <th className="text-center">Ngưỡng cao</th>
                      <th className="text-center">Ngưỡng OK</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModels.map(model => (
                      <tr key={model.id}>
                        <td>
                          <span className={`badge bg-${model.status === 'active' ? 'success' : 'secondary'}`}>
                            {model.status === 'active' ? '✅ Active' : '⏸️ Inactive'}
                          </span>
                        </td>
                        <td><code>{model.id}</code></td>
                        <td>
                          <strong>{model.name}</strong>
                          <div className="small text-muted">{model.description}</div>
                        </td>
                        <td><small>{model.targetObject}</small></td>
                        <td className="text-center">
                          <span className="badge bg-primary">{model.indicators.length}</span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex gap-1 justify-content-center flex-wrap">
                            {model.assignedCustomers && model.assignedCustomers.length > 0 && (
                              <span className="badge bg-info text-dark">
                                <i className="bi bi-person-check"></i> {model.assignedCustomers.length}
                              </span>
                            )}
                            {model.assignedCustomerGroups && model.assignedCustomerGroups.length > 0 && (
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-people-fill"></i> {model.assignedCustomerGroups.length}
                              </span>
                            )}
                            {(!model.assignedCustomers || model.assignedCustomers.length === 0) &&
                             (!model.assignedCustomerGroups || model.assignedCustomerGroups.length === 0) && (
                              <span className="text-muted small">-</span>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="text-danger fw-bold">≥{model.thresholds.high.min}</span>
                        </td>
                        <td className="text-center">
                          <span className="text-success fw-bold">&lt;{model.thresholds.none.max}</span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={() => { setQuickViewModel(model); setShowQuickViewModal(true); }} title="Xem nhanh">
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => { setEditingModel(model); setFormData({...model}); setShowModal(true); }} title="Sửa">
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className={`btn btn-outline-${model.status === 'active' ? 'warning' : 'success'}`} onClick={() => handleToggleStatus(model.id)} title={model.status === 'active' ? 'Tạm dừng' : 'Kích hoạt'}>
                              <i className={`bi bi-${model.status === 'active' ? 'pause' : 'play'}-circle`}></i>
                            </button>
                            <button className="btn btn-outline-danger" onClick={() => handleDelete(model.id)} title="Xóa">
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto'}}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header border-bottom-0 pb-0">
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">
                      <i className="bi bi-diagram-3 me-2"></i>
                      {editingModel ? 'Sửa' : 'Tạo'} mô hình
                      {formData.status === 'draft' && (
                        <span className="badge bg-secondary ms-2">Nháp</span>
                      )}
                    </h5>
                    <button className="btn-close" onClick={() => { setShowModal(false); setActiveFormTab('info'); }}></button>
                  </div>
                  
                  {/* Tab Navigation */}
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${activeFormTab === 'info' ? 'active' : ''} d-flex align-items-center gap-2`}
                        onClick={() => setActiveFormTab('info')}
                        type="button"
                      >
                        <i className={`bi ${getTabIcon('info')}`}></i>
                        <span className="d-none d-md-inline">{getTabLabel('info')}</span>
                        {isTabCompleted('info') && <i className="bi bi-check-circle-fill text-success"></i>}
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${activeFormTab === 'indicators' ? 'active' : ''} d-flex align-items-center gap-2`}
                        onClick={() => setActiveFormTab('indicators')}
                        type="button"
                      >
                        <i className={`bi ${getTabIcon('indicators')}`}></i>
                        <span className="d-none d-md-inline">{getTabLabel('indicators')}</span>
                        {isTabCompleted('indicators') && <i className="bi bi-check-circle-fill text-success"></i>}
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button 
                        className={`nav-link ${activeFormTab === 'assignment' ? 'active' : ''} d-flex align-items-center gap-2`}
                        onClick={() => setActiveFormTab('assignment')}
                        type="button"
                      >
                        <i className={`bi ${getTabIcon('assignment')}`}></i>
                        <span className="d-none d-md-inline">{getTabLabel('assignment')}</span>
                        {isTabCompleted('assignment') && <i className="bi bi-check-circle-fill text-success"></i>}
                        <span className="badge bg-info text-dark ms-1">Tùy chọn</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body" style={{paddingBottom: '100px'}}>
                  {/* Tab Content */}
                  <div className="tab-content">
                    
                    {/* Tab 1: Thông tin mô hình */}
                    {activeFormTab === 'info' && (
                      <div className="tab-pane fade show active">
                        <h6 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-info-circle me-2"></i>Thông tin cơ bản
                        </h6>
                        <div className="row g-3 mb-4">
                          <div className="col-md-8">
                            <label className="form-label">Tên mô hình <span className="text-danger">*</span></label>
                            <input type="text" name="name" className="form-control" value={formData.name} onChange={handleInputChange} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Đối tượng</label>
                            <select name="targetObject" className="form-select" value={formData.targetObject} onChange={handleInputChange}>
                              <option>Khách hàng cá nhân</option>
                              <option>Khách hàng doanh nghiệp</option>
                              <option>Các ĐCTC</option>
                            </select>
                          </div>
                          <div className="col-12">
                            <label>Mô tả</label>
                            <textarea name="description" className="form-control" rows="3" value={formData.description} onChange={handleInputChange} placeholder="Nhập mô tả chi tiết về mô hình..."></textarea>
                          </div>
                        </div>

                        <div className="alert alert-info">
                          <i className="bi bi-lightbulb me-2"></i>
                          <strong>Hướng dẫn:</strong> Nhập thông tin cơ bản về mô hình. Sau khi hoàn tất, bấm <strong>"Tiếp theo"</strong> để gán chỉ tiêu và cấu hình ngưỡng rủi ro.
                        </div>
                      </div>
                    )}

                    {/* Tab 2: Gán chỉ tiêu & Rủi ro */}
                    {activeFormTab === 'indicators' && (
                      <div className="tab-pane fade show active">
                        <h6 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-bar-chart me-2"></i>Gán chỉ tiêu từ kho dữ liệu
                        </h6>
                  
                  <div className="alert alert-info mb-3">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Lưu ý:</strong> Danh sách chỉ tiêu bên dưới được lấy từ module <strong>Quản lý Chỉ tiêu</strong> (chỉ hiển thị chỉ tiêu đang hoạt động)
                    </small>
                  </div>

                  <div className="card mb-3">
                    <div className="card-header bg-light">
                      <strong>Thêm chỉ tiêu vào mô hình</strong>
                      <small className="text-muted ms-2">({availableIndicators.length} chỉ tiêu có sẵn)</small>
                    </div>
                    <div className="card-body">
                      {/* Search hint */}
                      <div className="alert alert-light border mb-3">
                        <small className="text-muted">
                          <i className="bi bi-lightbulb me-1"></i>
                          <strong>Mẹo:</strong> Các chỉ tiêu được nhóm theo danh mục. Bạn có thể gõ để tìm kiếm theo tên, mã hoặc danh mục (VD: "tài chính", "IND-FIN", "thanh toán")
                        </small>
                      </div>

                      <div className="row g-3">
                        <div className="col-md-5">
                          <label className="form-label">Chọn chỉ tiêu</label>
                          <Select
                            options={indicatorOptions}
                            value={selectedIndicator}
                            onChange={setSelectedIndicator}
                            placeholder="🔍 Tìm kiếm chỉ tiêu (theo tên, mã, hoặc danh mục)..."
                            isClearable
                            isSearchable
                            formatGroupLabel={formatGroupLabel}
                            formatOptionLabel={formatOptionLabel}
                            filterOption={customFilterOption}
                            noOptionsMessage={() => 'Không tìm thấy chỉ tiêu phù hợp'}
                            styles={{
                              control: (base) => ({ ...base, minHeight: '42px' }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused ? '#e7f3ff' : 'white',
                                cursor: 'pointer',
                                padding: '8px 12px'
                              }),
                              groupHeading: (base) => ({
                                ...base,
                                padding: '0',
                                margin: '8px 12px 0 12px'
                              }),
                              menu: (base) => ({
                                ...base,
                                maxHeight: '400px'
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: '400px'
                              })
                            }}
                          />
                        </div>
                        <div className="col-md-3">
                          <label>Trọng số <span className="badge bg-primary">{indicatorWeight}</span></label>
                          <input type="range" className="form-range" min="0" max="1" step="0.01" value={indicatorWeight} onChange={(e) => setIndicatorWeight(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                          <label>Tần suất</label>
                          <select className="form-select" value={indicatorFrequency} onChange={(e) => setIndicatorFrequency(e.target.value)}>
                            <option>Hàng ngày</option>
                            <option>Hàng tuần</option>
                            <option>Hàng tháng</option>
                            <option>Hàng quý</option>
                          </select>
                        </div>
                        <div className="col-md-1 d-flex align-items-end">
                          <button type="button" className="btn btn-success w-100" onClick={handleAddIndicator}>
                            <i className="bi bi-plus-lg"></i>
                          </button>
                        </div>
                      </div>

                      {/* Preview selected indicator */}
                      {selectedIndicator && (() => {
                        const indicator = getIndicatorById(selectedIndicator.value);
                        if (!indicator) return null;
                        
                        const categoryColors = {
                          'Tài chính': 'success',
                          'Thanh toán': 'primary',
                          'Hành vi': 'warning',
                          'Bên ngoài': 'info',
                          'Khác': 'secondary'
                        };
                        const color = categoryColors[indicator.category] || 'secondary';
                        
                        return (
                          <div className="mt-3 p-3 border rounded bg-light">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="mb-1">
                                  <span className={`badge bg-${color} me-2`}>
                                    {indicator.category}
                                  </span>
                                  {indicator.name}
                                </h6>
                                <small className="text-muted">{indicator.id}</small>
                              </div>
                              <span className={`badge bg-${indicator.status === 'active' ? 'success' : 'secondary'}`}>
                                {indicator.status === 'active' ? 'Đang hoạt động' : 'Vô hiệu hóa'}
                              </span>
                            </div>
                            <p className="small text-muted mb-2">{indicator.description || 'Không có mô tả'}</p>
                            <div className="row g-2 small">
                              <div className="col-md-3">
                                <i className="bi bi-calculator me-1 text-muted"></i>
                                <strong>Đơn vị:</strong> {indicator.unit}
                              </div>
                              <div className="col-md-4">
                                <i className="bi bi-database me-1 text-muted"></i>
                                <strong>Nguồn:</strong> {indicator.dataSource}
                              </div>
                              <div className="col-md-5">
                                <i className="bi bi-bullseye me-1 text-muted"></i>
                                <strong>Đối tượng:</strong> {indicator.targetObject}
                              </div>
                            </div>
                            {indicator.thresholds && indicator.thresholds.length > 0 && (
                              <div className="mt-2">
                                <small className="text-muted d-block mb-1">Ngưỡng cảnh báo:</small>
                                <div className="d-flex gap-1">
                                  <div className="flex-fill" style={{ height: '6px', background: '#dc3545', borderRadius: '3px' }} title="Cao"></div>
                                  <div className="flex-fill" style={{ height: '6px', background: '#ffc107', borderRadius: '3px' }} title="TB"></div>
                                  <div className="flex-fill" style={{ height: '6px', background: '#17a2b8', borderRadius: '3px' }} title="Thấp"></div>
                                  <div className="flex-fill" style={{ height: '6px', background: '#28a745', borderRadius: '3px' }} title="OK"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {formData.indicators.length > 0 && (
                    <div className="card mb-4">
                      <div className="card-header bg-success bg-opacity-10 d-flex justify-content-between align-items-center">
                        <span><i className="bi bi-collection-fill me-2"></i>Danh sách chỉ tiêu đã gán ({formData.indicators.length})</span>
                        <span className={`badge ${Math.abs(parseFloat(getTotalWeight()) - 1.0) < 0.01 ? 'bg-success' : 'bg-warning'}`}>
                          Tổng trọng số: {getTotalWeight()}
                        </span>
                      </div>
                      
                      {/* Search and Filter */}
                      {formData.indicators.length > 2 && (
                        <div className="card-body border-bottom bg-light">
                          <div className="row g-2">
                            <div className="col-md-8">
                              <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                  <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                  type="text"
                                  className="form-control border-start-0 ps-0"
                                  placeholder="Tìm kiếm chỉ tiêu theo tên hoặc mã..."
                                  value={indicatorSearchTerm}
                                  onChange={(e) => setIndicatorSearchTerm(e.target.value)}
                                />
                                {indicatorSearchTerm && (
                                  <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setIndicatorSearchTerm('')}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="col-md-4">
                              <select
                                className="form-select form-select-sm"
                                value={selectedIndicatorCategory}
                                onChange={(e) => setSelectedIndicatorCategory(e.target.value)}
                              >
                                <option value="">Tất cả danh mục</option>
                                <option value="Tài chính">Tài chính</option>
                                <option value="Thanh toán">Thanh toán</option>
                                <option value="Hành vi">Hành vi</option>
                                <option value="Bên ngoài">Bên ngoài</option>
                                <option value="Khác">Khác</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="card-body">
                        <div className="row g-3">
                          {formData.indicators
                            .map((ind, i) => {
                              const indicator = getIndicatorById(ind.id);
                              if (!indicator) return null;
                              
                              // Apply search filter
                              const searchLower = indicatorSearchTerm.toLowerCase();
                              const matchesSearch = !indicatorSearchTerm || 
                                indicator.name.toLowerCase().includes(searchLower) ||
                                indicator.id.toLowerCase().includes(searchLower) ||
                                (indicator.description && indicator.description.toLowerCase().includes(searchLower));
                              
                              // Apply category filter
                              const matchesCategory = !selectedIndicatorCategory || 
                                indicator.category === selectedIndicatorCategory;
                              
                              if (!matchesSearch || !matchesCategory) return null;
                              
                              const categoryColors = {
                                'Tài chính': 'success',
                                'Thanh toán': 'primary',
                                'Hành vi': 'warning',
                                'Bên ngoài': 'info',
                                'Khác': 'secondary'
                              };
                              const color = categoryColors[indicator.category] || 'secondary';
                              
                              return (
                              <div key={i} className="col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid var(--bs-${color})` }}>
                                  <div className="card-body p-3">
                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                          <span className={`badge bg-${color}`}>
                                            {indicator.category}
                                          </span>
                                          <small className="text-muted">{indicator.id}</small>
                                        </div>
                                        <h6 className="mb-0">{indicator.name}</h6>
                                      </div>
                                      <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setFormData(prev => ({
                                          ...prev, 
                                          indicators: prev.indicators.filter((_, idx) => idx !== i)
                                        }))}
                                        title="Xóa chỉ tiêu"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>

                                    {/* Description */}
                                    <p className="small text-muted mb-2" style={{ minHeight: '32px' }}>
                                      {indicator.description || 'Không có mô tả'}
                                    </p>

                                    {/* Info badges */}
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                      <small className="badge bg-light text-dark border">
                                        <i className="bi bi-calculator me-1"></i>{indicator.unit}
                                      </small>
                                      <small className="badge bg-light text-dark border">
                                        <i className="bi bi-database me-1"></i>{indicator.dataSource}
                                      </small>
                                      <small className="badge bg-light text-dark border">
                                        <i className="bi bi-bullseye me-1"></i>{indicator.targetObject}
                                      </small>
                                    </div>

                                    {/* Threshold bar */}
                                    {indicator.thresholds && indicator.thresholds.length > 0 && (
                                      <div className="mb-3">
                                        <small className="text-muted d-block mb-1">Ngưỡng cảnh báo:</small>
                                        <div className="d-flex gap-1">
                                          <div className="flex-fill" style={{ height: '6px', background: '#dc3545', borderRadius: '3px' }} title="Cao"></div>
                                          <div className="flex-fill" style={{ height: '6px', background: '#ffc107', borderRadius: '3px' }} title="TB"></div>
                                          <div className="flex-fill" style={{ height: '6px', background: '#17a2b8', borderRadius: '3px' }} title="Thấp"></div>
                                          <div className="flex-fill" style={{ height: '6px', background: '#28a745', borderRadius: '3px' }} title="OK"></div>
                                        </div>
                                        <div className="d-flex justify-content-between mt-1">
                                          <small className="text-danger" style={{ fontSize: '0.7rem' }}>Cao</small>
                                          <small className="text-warning" style={{ fontSize: '0.7rem' }}>TB</small>
                                          <small className="text-info" style={{ fontSize: '0.7rem' }}>Thấp</small>
                                          <small className="text-success" style={{ fontSize: '0.7rem' }}>OK</small>
                                        </div>
                                      </div>
                                    )}

                                    {/* Weight slider */}
                                    <div className="mb-2">
                                      <label className="form-label small mb-1">
                                        Trọng số: <span className="badge bg-primary ms-1">{ind.weight.toFixed(2)}</span>
                                      </label>
                                      <input 
                                        type="range" 
                                        className="form-range" 
                                        min="0" 
                                        max="1" 
                                        step="0.01" 
                                        value={ind.weight} 
                                        onChange={(e) => {
                                          const newIndicators = [...formData.indicators];
                                          newIndicators[i].weight = parseFloat(e.target.value);
                                          setFormData(prev => ({ ...prev, indicators: newIndicators }));
                                        }}
                                      />
                                    </div>

                                    {/* Frequency select */}
                                    <div>
                                      <label className="form-label small mb-1">Tần suất thu thập:</label>
                                      <select 
                                        className="form-select form-select-sm" 
                                        value={ind.frequency}
                                        onChange={(e) => {
                                          const newIndicators = [...formData.indicators];
                                          newIndicators[i].frequency = e.target.value;
                                          setFormData(prev => ({ ...prev, indicators: newIndicators }));
                                        }}
                                      >
                                        <option>Hàng ngày</option>
                                        <option>Hàng tuần</option>
                                        <option>Hàng tháng</option>
                                        <option>Hàng quý</option>
                                      </select>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                        {/* Threshold Configuration */}
                        <h6 className="border-bottom pb-2 mb-3 mt-4">
                          <i className="bi bi-exclamation-triangle me-2"></i>Ngưỡng cảnh báo
                        </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="card border-danger">
                        <div className="card-header bg-danger bg-opacity-10 py-2">
                          <small className="text-danger fw-bold">🔴 Rủi ro cao</small>
                        </div>
                        <div className="card-body p-3">
                          <div className="row g-2">
                            <div className="col-6">
                              <label className="form-label small">Giá trị (min)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 4.0"
                                value={formData.thresholds.high.min || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    high: { ...prev.thresholds.high, min: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small">Giá trị (max)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="Không giới hạn"
                                value={formData.thresholds.high.max || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    high: { ...prev.thresholds.high, max: parseFloat(e.target.value) || undefined }
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-warning">
                        <div className="card-header bg-warning bg-opacity-10 py-2">
                          <small className="text-warning fw-bold">🟡 Rủi ro TB</small>
                        </div>
                        <div className="card-body p-3">
                          <div className="row g-2">
                            <div className="col-6">
                              <label className="form-label small">Giá trị (min)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 2.0"
                                value={formData.thresholds.medium.min || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    medium: { ...prev.thresholds.medium, min: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small">Giá trị (max)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 3.99"
                                value={formData.thresholds.medium.max || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    medium: { ...prev.thresholds.medium, max: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-info">
                        <div className="card-header bg-info bg-opacity-10 py-2">
                          <small className="text-info fw-bold">🔵 Rủi ro thấp</small>
                        </div>
                        <div className="card-body p-3">
                          <div className="row g-2">
                            <div className="col-6">
                              <label className="form-label small">Giá trị (min)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 1.0"
                                value={formData.thresholds.low.min || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    low: { ...prev.thresholds.low, min: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small">Giá trị (max)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 1.99"
                                value={formData.thresholds.low.max || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    low: { ...prev.thresholds.low, max: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-header bg-success bg-opacity-10 py-2">
                          <small className="text-success fw-bold">🟢 Không rủi ro</small>
                        </div>
                        <div className="card-body p-3">
                          <div className="row g-2">
                            <div className="col-6">
                              <label className="form-label small">Giá trị (min)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 0"
                                value={formData.thresholds.none.min || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    none: { ...prev.thresholds.none, min: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label small">Giá trị (max)</label>
                              <input 
                                type="number" 
                                step="0.01"
                                className="form-control form-control-sm" 
                                placeholder="VD: 0.99"
                                value={formData.thresholds.none.max || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  thresholds: {
                                    ...prev.thresholds,
                                    none: { ...prev.thresholds.none, max: parseFloat(e.target.value) || 0 }
                                  }
                                }))}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="alert alert-info">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Gợi ý:</strong> Ngưỡng cảnh báo dựa trên tổng điểm rủi ro được tính từ trọng số các chỉ tiêu. 
                      Ví dụ: Nếu mô hình có 4 chỉ tiêu với trọng số 0.25 mỗi chỉ tiêu, điểm tối đa là 4.0.
                    </small>
                  </div>
                      </div>
                    )}

                    {/* Tab 3: Gán khách hàng */}
                    {activeFormTab === 'assignment' && (
                      <div className="tab-pane fade show active">
                        <h6 className="border-bottom pb-2 mb-3">
                          <i className="bi bi-people me-2"></i>Gán khách hàng & nhóm khách hàng
                        </h6>
                        
                        <div className="alert alert-info mb-3">
                          <small>
                            <i className="bi bi-info-circle me-1"></i>
                            <strong>Tùy chọn:</strong> Bạn có thể gán mô hình cho khách hàng cụ thể hoặc nhóm khách hàng. Mô hình sẽ được áp dụng tự động khi chạy cảnh báo cho các khách hàng/nhóm được gán.
                          </small>
                        </div>

                        {/* Inner Tab Navigation for customers/groups */}
                        <ul className="nav nav-tabs mb-3" role="tablist">
                          <li className="nav-item" role="presentation">
                            <button 
                              className={`nav-link ${activeAssignTab === 'customers' ? 'active' : ''}`}
                              onClick={() => setActiveAssignTab('customers')}
                              type="button"
                            >
                              <i className="bi bi-person me-1"></i>
                              Khách hàng
                              {formData.assignedCustomers && formData.assignedCustomers.length > 0 && (
                                <span className="badge bg-primary ms-2">{formData.assignedCustomers.length}</span>
                              )}
                            </button>
                          </li>
                          <li className="nav-item" role="presentation">
                            <button 
                              className={`nav-link ${activeAssignTab === 'groups' ? 'active' : ''}`}
                              onClick={() => setActiveAssignTab('groups')}
                              type="button"
                            >
                              <i className="bi bi-people-fill me-1"></i>
                              Nhóm khách hàng
                              {formData.assignedCustomerGroups && formData.assignedCustomerGroups.length > 0 && (
                                <span className="badge bg-warning text-dark ms-2">{formData.assignedCustomerGroups.length}</span>
                              )}
                            </button>
                          </li>
                        </ul>

                        {/* Inner Tab Content */}
                        <div className="tab-content mb-4">
                          {/* Customers inner tab */}
                          {activeAssignTab === 'customers' && (
                            <div className="tab-pane fade show active">
                              <div className="mb-3">
                                <label className="form-label fw-bold">
                                  <i className="bi bi-person-plus me-1"></i>Chọn khách hàng
                                </label>
                                <Select
                                  isMulti
                                  options={customers.map(c => ({ 
                                    value: c.id, 
                                    label: `${c.code} - ${c.name}`
                                  }))}
                                  value={(formData.assignedCustomers || []).map(id => {
                                    const customer = customers.find(c => c.id === id);
                                    return customer ? { 
                                      value: customer.id, 
                                      label: `${customer.code} - ${customer.name}`
                                    } : null;
                                  }).filter(Boolean)}
                                  onChange={(selected) => setFormData(prev => ({ 
                                    ...prev, 
                                    assignedCustomers: selected ? selected.map(s => s.value) : [] 
                                  }))}
                                  placeholder="Tìm và chọn khách hàng..."
                                  isClearable
                                  isSearchable
                                  noOptionsMessage={() => 'Không tìm thấy khách hàng'}
                                  styles={{
                                    control: (base) => ({ ...base, minHeight: '42px' }),
                                    multiValue: (base) => ({ ...base, backgroundColor: '#e3f2fd' }),
                                    multiValueLabel: (base) => ({ ...base, color: '#1976d2', fontWeight: 500 })
                                  }}
                                />
                                <small className="text-muted mt-1 d-block">
                                  Đã chọn: {(formData.assignedCustomers || []).length} khách hàng
                                </small>
                              </div>

                              {formData.assignedCustomers && formData.assignedCustomers.length > 0 ? (
                                <div className="row g-2 mt-2">
                                  {formData.assignedCustomers.map(customerId => {
                                    const customer = customers.find(c => c.id === customerId);
                                    if (!customer) return null;
                                    return (
                                      <div key={customerId} className="col-md-4">
                                        <div className="card border-0 shadow-sm h-100">
                                          <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start">
                                              <div className="flex-grow-1">
                                                <small className="text-primary fw-bold">{customer.code}</small>
                                                <div className="small fw-bold">{customer.name}</div>
                                              </div>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger p-1"
                                                style={{ lineHeight: 1, fontSize: '0.7rem' }}
                                                onClick={() => setFormData(prev => ({
                                                  ...prev,
                                                  assignedCustomers: prev.assignedCustomers.filter(id => id !== customerId)
                                                }))}
                                              >
                                                <i className="bi bi-x"></i>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-3 text-muted">
                                  <small>Chưa có khách hàng nào được chọn</small>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Groups inner tab */}
                          {activeAssignTab === 'groups' && (
                            <div className="tab-pane fade show active">
                              <div className="mb-3">
                                <label className="form-label fw-bold">
                                  <i className="bi bi-people-fill me-1"></i>Chọn nhóm khách hàng
                                </label>
                                <Select
                                  isMulti
                                  options={customerGroups.map(g => ({ 
                                    value: g.id, 
                                    label: `${g.name} (${g.category})`
                                  }))}
                                  value={(formData.assignedCustomerGroups || []).map(id => {
                                    const group = customerGroups.find(g => g.id === id);
                                    return group ? { 
                                      value: group.id, 
                                      label: `${group.name} (${group.category})`
                                    } : null;
                                  }).filter(Boolean)}
                                  onChange={(selected) => setFormData(prev => ({ 
                                    ...prev, 
                                    assignedCustomerGroups: selected ? selected.map(s => s.value) : [] 
                                  }))}
                                  placeholder="Tìm và chọn nhóm khách hàng..."
                                  isClearable
                                  isSearchable
                                  noOptionsMessage={() => 'Không tìm thấy nhóm'}
                                  styles={{
                                    control: (base) => ({ ...base, minHeight: '42px' }),
                                    multiValue: (base) => ({ ...base, backgroundColor: '#fff3e0' }),
                                    multiValueLabel: (base) => ({ ...base, color: '#e65100', fontWeight: 500 })
                                  }}
                                />
                                <small className="text-muted mt-1 d-block">
                                  Đã chọn: {(formData.assignedCustomerGroups || []).length} nhóm
                                </small>
                              </div>

                              {formData.assignedCustomerGroups && formData.assignedCustomerGroups.length > 0 ? (
                                <div className="row g-2 mt-2">
                                  {formData.assignedCustomerGroups.map(groupId => {
                                    const group = customerGroups.find(g => g.id === groupId);
                                    if (!group) return null;
                                    return (
                                      <div key={groupId} className="col-md-6">
                                        <div className="card border-0 shadow-sm h-100">
                                          <div className="card-body p-2">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                              <div className="flex-grow-1">
                                                <div className="small fw-bold">{group.name}</div>
                                                <span className="badge bg-info text-dark" style={{ fontSize: '0.7rem' }}>{group.category}</span>
                                              </div>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger p-1"
                                                style={{ lineHeight: 1, fontSize: '0.7rem' }}
                                                onClick={() => setFormData(prev => ({
                                                  ...prev,
                                                  assignedCustomerGroups: prev.assignedCustomerGroups.filter(id => id !== groupId)
                                                }))}
                                              >
                                                <i className="bi bi-x"></i>
                                              </button>
                                            </div>
                                            <small className="text-muted">{group.customerIds.length} khách hàng</small>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-3 text-muted">
                                  <small>Chưa có nhóm nào được chọn</small>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="alert alert-info">
                          <i className="bi bi-lightbulb me-2"></i>
                          <strong>Lưu ý:</strong> Việc gán khách hàng là tùy chọn. Bạn có thể bỏ qua bước này và hoàn thành tạo mô hình.
                        </div>
                      </div>
                    )}

                  </div>
                  {/* End of tab-content */}

                </div>
                {/* End of modal-body */}

                <div 
                  className="modal-footer d-flex justify-content-between" 
                  style={{
                    position: 'sticky', 
                    bottom: 0, 
                    zIndex: 1000, 
                    backgroundColor: '#fff',
                    borderTop: '2px solid #dee2e6',
                    boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
                    marginTop: 0
                  }}
                >
                  {/* Left side: Status indicators */}
                  <div className="d-flex align-items-center gap-2">
                    {activeFormTab === 'indicators' && (
                      <div className="alert alert-warning mb-0 py-2 px-3">
                        <small>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          <strong>Tổng trọng số:</strong>
                          {Math.abs(parseFloat(getTotalWeight()) - 1.0) >= 0.01 ? (
                            <span className="text-danger ms-2 fw-bold">{getTotalWeight()} (phải = 1.0)</span>
                          ) : (
                            <span className="text-success ms-2">✓ {getTotalWeight()}</span>
                          )}
                        </small>
                      </div>
                    )}
                    
                    {!editingModel && (
                      <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={handleSaveDraft}
                      >
                        <i className="bi bi-file-earmark me-1"></i>
                        Lưu nháp
                      </button>
                    )}
                  </div>

                  {/* Right side: Navigation buttons */}
                  <div className="d-flex gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={() => { setShowModal(false); setActiveFormTab('info'); }}
                    >
                      Hủy
                    </button>
                    
                    {activeFormTab !== 'info' && (
                      <button 
                        type="button" 
                        className="btn btn-outline-primary"
                        onClick={handlePrevTab}
                      >
                        <i className="bi bi-arrow-left me-1"></i>
                        Quay lại
                      </button>
                    )}
                    
                    {activeFormTab !== 'assignment' && (
                      <button 
                        type="button" 
                        className="btn btn-primary"
                        onClick={handleNextTab}
                        disabled={!canProceedToNextTab()}
                      >
                        Tiếp theo
                        <i className="bi bi-arrow-right ms-1"></i>
                      </button>
                    )}
                    
                    {activeFormTab === 'assignment' && (
                      <button 
                        type="submit" 
                        className="btn btn-success"
                        disabled={!isTabCompleted('indicators')}
                      >
                        <i className="bi bi-check-circle me-1"></i>
                        {editingModel ? 'Cập nhật mô hình' : 'Hoàn thành'}
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Quick View Modal */}
      {showQuickViewModal && quickViewModel && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5><i className="bi bi-eye me-2"></i>Chi tiết: {quickViewModel.name}</h5>
                <button className="btn-close" onClick={() => setShowQuickViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="text-muted small">Mã</label>
                    <div><strong>{quickViewModel.id}</strong></div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">Đối tượng</label>
                    <div>{quickViewModel.targetObject}</div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">Mô tả</label>
                    <div>{quickViewModel.description || 'Không có mô tả'}</div>
                  </div>
                </div>
                <h6 className="border-bottom pb-2">Chỉ tiêu trong mô hình ({quickViewModel.indicators.length})</h6>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>STT</th>
                      <th>Mã</th>
                      <th>Tên</th>
                      <th>Trọng số</th>
                      <th>Tần suất</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quickViewModel.indicators.map((ind, i) => (
                      <tr key={i}>
                        <td>{i+1}</td>
                        <td><span className="badge bg-secondary small">{ind.id}</span></td>
                        <td>{getIndicatorName(ind.id)}</td>
                        <td><span className="badge bg-primary">{ind.weight.toFixed(2)}</span></td>
                        <td>{ind.frequency}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan="3" className="text-end"><strong>Tổng:</strong></td>
                      <td colSpan="2">
                        <span className="badge bg-success">
                          {quickViewModel.indicators.reduce((sum, ind) => sum + ind.weight, 0).toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowQuickViewModal(false)}>Đóng</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Models;
